import { Setting } from '../types/settings.types';

/**
 * Whether an edited value is back where it started.
 *
 * The controls hand back strings: a boolean toggle sends 'true', a number
 * field sends '20'. `typed_value` holds a real boolean or number. So a
 * plain !== between them is always true, and putting a toggle back where
 * you found it left it counted as an unsaved change forever, with the save
 * bar stuck on and the setting resubmitted on the next save.
 */
export function isSameSettingValue(
  setting: Pick<Setting, 'data_type'>,
  value: unknown,
  initial: unknown,
): boolean {
  switch (setting.data_type) {
    case 'BOOLEAN': {
      const on = (v: any) => v === true || v === 'true';
      return on(value) === on(initial);
    }
    case 'INTEGER':
    case 'DECIMAL': {
      // Number('') is 0, so an emptied field would otherwise read as equal
      // to a stored zero. Anything that is not a usable number falls back
      // to text, which keeps '' distinct from 0.
      const numeric = (v: any) =>
        v !== '' && v !== null && v !== undefined && !Number.isNaN(Number(v));
      if (!numeric(value) || !numeric(initial)) {
        return String(value ?? '') === String(initial ?? '');
      }
      return Number(value) === Number(initial);
    }
    case 'JSON':
      try {
        const norm = (v: any) => JSON.stringify(typeof v === 'string' ? JSON.parse(v) : v);
        return norm(value) === norm(initial);
      } catch {
        return String(value ?? '') === String(initial ?? '');
      }
    default:
      return String(value ?? '') === String(initial ?? '');
  }
}
